"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { rpc } from "#rpc";
import type { CategorySources, PLPPage } from "../types";
import { usePLPFilters } from "./usePLPFilters";

const PAGE_QUERY_PARAM = "page";
const MAX_HYDRATE_PAGES = 50;

/**
 * Read `?page=N` from the URL once on mount. Returns 1 on the server (no
 * window), so the initial SSR render always matches the client's first
 * render — only after hydration does the auto-walk effect kick in.
 */
const useDeepLinkTarget = (): number => {
	const [target] = useState(() => {
		if (typeof window === "undefined") return 1;
		const raw = new URLSearchParams(window.location.search).get(PAGE_QUERY_PARAM);
		const parsed = raw ? Number.parseInt(raw, 10) : NaN;
		if (!Number.isFinite(parsed) || parsed < 1) return 1;
		return Math.min(MAX_HYDRATE_PAGES, parsed);
	});
	return target;
};

/**
 * Mirror the current page number into the URL via `history.replaceState`.
 * Uses `replaceState` (not `pushState`) so each "Load More" doesn't pollute
 * the back stack — back should still take the user to the page before the
 * PLP, not step backwards through pagination.
 *
 * Skips while we're still walking toward the deep-link target so the URL
 * doesn't briefly downgrade to `?page=1` before catching up. Also skips
 * while a filter/sort is active — filtered URLs don't carry `?page`.
 */
const useSyncPageParam = (currentPage: number, hydrationTarget: number, isFiltered: boolean) => {
	useEffect(() => {
		if (typeof window === "undefined") return;
		if (currentPage < hydrationTarget) return;
		const url = new URL(window.location.href);
		if (isFiltered || currentPage <= 1) {
			url.searchParams.delete(PAGE_QUERY_PARAM);
		} else {
			url.searchParams.set(PAGE_QUERY_PARAM, String(currentPage));
		}
		if (url.search !== window.location.search) {
			window.history.replaceState(null, "", url.toString());
		}
	}, [currentPage, hydrationTarget, isFiltered]);
};

const filterKeysFromPage = (page: PLPPage) => page.filters.map((f) => f.key);

/**
 * Drives the PLP infinite query. The source (Depict or Centra) is locked at
 * SSR — every paginated call uses the same source, and the route terminates
 * pagination cleanly if Depict starts failing mid-session.
 *
 * Filter + sort state lives in the URL. When either changes, the query key
 * changes and React Query refetches from page 1 — initialData seeds the
 * unfiltered SSR page, so an active filter on first load triggers a refetch.
 *
 * Deep-link hydration: on mount, if the URL carries `?page=N` (and no active
 * filter), the hook auto-fires `fetchNextPage()` until N pages are loaded.
 * Each call is permacached server-side, so the walk is cheap on warm cache.
 */
export const usePLPListingQuery = ({
	initialPage,
	categorySources,
	searchQuery,
	forceClientFetch,
	preview,
}: {
	initialPage: PLPPage;
	categorySources: CategorySources;
	/** When set, the underlying RPC switches to search mode (query overrides category). */
	searchQuery?: string;
	/**
	 * When true, the SSR seed (`initialData`) is dropped and `staleTime` flips
	 * from infinity to zero so the hook always fetches fresh from the client.
	 * Used in Storyblok preview mode where SSR is skipped on purpose — the
	 * editor sees live changes without waiting for ISR revalidation.
	 */
	forceClientFetch?: boolean;
	/**
	 * Storyblok preview render — forwarded to the RPC so filter/pagination
	 * fetches keep the Norway geo fallback when the iframe has no Centra cookie.
	 */
	preview?: boolean;
}) => {
	const { depictCategoryId, centraCategoryId } = categorySources;
	const { filters, sortId, isFiltered, ...filterActions } = usePLPFilters(
		filterKeysFromPage(initialPage),
	);

	const rpcInput = {
		depictCategory: initialPage.source === "depict" ? depictCategoryId || undefined : undefined,
		centraCategory: centraCategoryId,
		query: searchQuery,
		filters,
		sortId,
		preview,
	};

	const query = useInfiniteQuery({
		queryKey: [
			"plp",
			initialPage.source,
			searchQuery ?? null,
			depictCategoryId,
			centraCategoryId,
			filters,
			sortId ?? null,
		],
		queryFn: ({ pageParam }) => rpc.plp.list({ ...rpcInput, pageParam }),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => lastPage.nextPageParam ?? undefined,
		// Keep the previous result visible while filter/sort changes refetch —
		// otherwise the filter list flashes empty and the grid blanks out.
		placeholderData: keepPreviousData,
		// SSR seed only valid for the unfiltered/unsorted base case. If the URL
		// carries filter state on first load, React Query refetches because the
		// query key differs from this seeded key. In preview mode we also drop
		// the seed so the client fetches fresh on every visit.
		initialData:
			isFiltered || forceClientFetch
				? undefined
				: {
						pages: [initialPage],
						pageParams: [undefined],
					},
		// We trust the SSR seed for the unfiltered base case — staleTime: Infinity
		// stops React Query from refetching on mount and clobbering the
		// SSR-ordered products. Filter/sort changes still refetch because they
		// produce a new query key; Load More still fetches new pages.
		// In preview mode we drop staleTime so editors see live content changes.
		staleTime: forceClientFetch ? 0 : Number.POSITIVE_INFINITY,
	});

	const targetPage = useDeepLinkTarget();
	const currentPage = query.data?.pages.length ?? 1;
	const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

	useEffect(() => {
		if (isFiltered) return;
		if (currentPage >= targetPage) return;
		if (!hasNextPage || isFetchingNextPage) return;
		fetchNextPage();
	}, [currentPage, targetPage, hasNextPage, isFetchingNextPage, fetchNextPage, isFiltered]);

	useSyncPageParam(currentPage, targetPage, isFiltered);

	// `initialPage` is the unfiltered SSR base. Exposing it lets callers fall
	// back to the canonical filter list when the active query produces zero
	// hits (Depict drops the facets in that case) — keeps every group visible
	// so the user can always deselect their way back to results.
	return { ...query, filters, sortId, isFiltered, initialPage, ...filterActions };
};
